import {
  DenormalizedFormat,
  DenormalizedListFormatsModel,
  ImageQualityInfoResponse,
  ImageRaw,
  WatermarkPosition,
  WatermarkSource
} from '@aipb/gulliver/dropbox/model';
import { uniqueId } from '@aipb/shared/util/other';
import { Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { withEntities } from '@ngneat/elf-entities';
import { Observable } from 'rxjs';
import { ImagePreviewItem, ImagePreviewProps } from '../model';

@Injectable()
export class ImagePreviewRepository {
  store = createStore(
    { name: 'image-preview-' + uniqueId() },
    withEntities<ImagePreviewItem>(),
    withProps<ImagePreviewProps>({
      resolutionData: {
        resAvailable: [],
        freeDownloadsLeft: 0,
        alreadyDownloaded: false,
        paidDownloadsAllowed: false,
        license: ''
      },
      listFormats: null,
      formats: [],
      activeId: null,
      images: {},
      imageRaws: {},
      loading: false
    })
  );

  formats$: Observable<DenormalizedFormat[]> = this.store.pipe(select((state) => state.formats));
  watermarkSources$: Observable<WatermarkSource[]> = this.store.pipe(
    select((state) => (state.listFormats ? state.listFormats.watermarks.sources : []))
  );
  watermarkPositions$: Observable<WatermarkPosition[]> = this.store.pipe(
    select((state) => (state.listFormats ? state.listFormats.watermarks.positions : []))
  );

  activeImage$ = this.store.pipe(
    select((state) => (state.activeId in state.images ? state.images[state.activeId] : null))
  );
  activeImageRaw$ = this.store.pipe(
    select((state) => (state.activeId in state.imageRaws ? state.imageRaws[state.activeId] : null))
  );
  activeId$ = this.store.pipe(select((state) => state.activeId));
  resolutionData$ = this.store.pipe(select((state) => state.resolutionData));

  loading$ = this.store.pipe(select((state) => state.loading));

  setImage(id: string, image: string) {
    this.store.update((state) => {
      return {
        ...state,
        images: {
          ...state.images,
          [id]: image
        }
      };
    });
  }

  setLoading(loading: boolean) {
    this.store.update((state) => ({
      ...state,
      loading
    }));
  }

  hasImage(id: string) {
    const images = this.store.getValue().images;

    return id in images;
  }

  hasImageRaw(id: string) {
    const imageRaws = this.store.getValue().imageRaws;

    return id in imageRaws;
  }

  getImageRaw(id: string) {
    const imageRaws = this.store.getValue().imageRaws;

    return imageRaws[id];
  }

  setImageRaw(id: string, imageRaw: ImageRaw) {
    this.store.update((state) => {
      return {
        ...state,
        imageRaws: {
          ...state.imageRaws,
          [id]: imageRaw
        }
      };
    });
  }

  setActiveId(id: string) {
    this.store.update((state) => {
      return {
        ...state,
        activeId: id
      };
    });
  }

  setFormats(data: { listFormats: DenormalizedListFormatsModel; formats: DenormalizedFormat[] }) {
    this.store.update((state) => {
      return {
        ...state,
        ...data
      };
    });
  }

  setResolutions(resolutions: ImageQualityInfoResponse) {
    this.store.update((state) => {
      return {
        ...state,
        resolutionData: resolutions
      };
    });
  }
}
